import { Inmate } from '@reliance/types/models/Inmate';
import { AudioVideoInmateActivity } from '@reliance/types/models/InmateActivity';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { formatCurrency } from '../../format/Currency';
import { useAudioVideoInmateActivitiesByInmateId } from '../../hooks/InmateActivity';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import AddActivityForm from '../../inmate-activity/AddActivityForm';
import { INMATE_ACTIVITY_TYPE_FILTER_OPTIONS } from '../../inmate-activity/inmateActivityTypeFilterOptions';
import BaseHtmlTable from '../../table/BaseHtmlTable';
import { useServerTableFilterState } from '../../table/useServerTableFilterState';
import { formatPhoneNumber, toE164 } from '../../format/PhoneNumber';

const COLUMNS = [
  { id: 'id', meta: { sortName: 'id' } },
  { id: 'dateFinalized', meta: { sortName: 'dateFinalized' } },
  { id: 'type', meta: { sortName: 'type' } },
  { id: 'amount', meta: { sortName: 'amount' } },
  { id: 'description', meta: { sortName: 'description' } },
];

const DEFAULT_DATE_RANGE_YEARS = 5;
const DEFAULT_PER_PAGE = 50;
const ACTIVITY_ORDER_BY = '-dateFinalized';

type InmateAvAccountProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
  onInmateUpdate?: () => void;
};

const InmateAvAccount: React.FC<InmateAvAccountProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger, onInmateUpdate } = props;

  const startDate = useMemo(
    () => moment().subtract(DEFAULT_DATE_RANGE_YEARS, 'year').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    []
  );
  const endDate = useMemo(
    () => moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    []
  );

  const baseRecord = useMemo(
    () =>
      ({
        inmateId: inmate.id,
        startDate,
        endDate,
        orderBy: ACTIVITY_ORDER_BY,
      }) as Record<string, unknown>,
    [inmate.id, startDate, endDate]
  );
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: DEFAULT_PER_PAGE },
    initialSorting: [{ id: 'dateFinalized', desc: true }],
  });
  const activityFilters = useMemo(
    () => ({
      ...tableState.mergedRecord,
      inmateId: inmate.id,
      orderBy: (tableState.mergedRecord.orderBy as string | undefined) ?? ACTIVITY_ORDER_BY,
    }),
    [inmate.id, tableState.mergedRecord]
  );

  const avActivitiesResult = useAudioVideoInmateActivitiesByInmateId(null, activityFilters) as {
    result?: { data: AudioVideoInmateActivity[]; total: number; supportingData?: { previousBalance?: number } };
    loading: boolean;
    execute: (id: number) => void;
  };

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        avActivitiesResult.execute(inmate.id);
      }
    }, [avActivitiesResult, inmate.id]),
  });

  useEffect(() => {
    if (tabSelected && inmate.id) {
      avActivitiesResult.execute(inmate.id);
    }
  }, [tabSelected, inmate.id, tableState.mergedRecord]);

  useNotifyTabLoading('av-account', avActivitiesResult.loading);

  const handleActivityAdded = useCallback(() => {
    if (inmate.id) {
      avActivitiesResult.execute(inmate.id);
      onInmateUpdate?.();
    }
  }, [inmate.id, avActivitiesResult, onInmateUpdate]);

  const balance = inmate.audioVideoBalance?.[0]?.balance;
  const activityData = avActivitiesResult.result?.data ?? [];
  const activityTotal = avActivitiesResult.result?.total ?? 0;
  const perPage = tableState.pagination.pageSize;
  const totalPages = Math.ceil(activityTotal / perPage) || 1;
  const page = tableState.pagination.pageIndex + 1;

  const columnHelper = createColumnHelper<AudioVideoInmateActivity>();
  const activityColumns = useMemo(
    () => [
      columnHelper.accessor('id', {
        id: 'id',
        header: 'ID',
        meta: { sortName: 'id' },
      }),
      columnHelper.accessor((row) => row.dateFinalized, {
        id: 'dateFinalized',
        header: 'Date',
        cell: (info) => {
          const val = info.getValue();
          if (!val) return '';
          const d = typeof val === 'object' && val && 'date' in val ? (val as { date: string }).date : String(val);
          return moment(d).format('MM/DD/YYYY HH:mm:ss');
        },
        meta: { sortName: 'dateFinalized' },
      }),
      columnHelper.accessor((row) => row.type, {
        id: 'type',
        header: 'Type',
        cell: (info) => {
          const row = info.row.original;
          const payment = row.payment;
          return (
            <>
              <span>{String(row.type)}</span>
              {payment && (
                <div className="small">
                  {payment.customerName && <span>{payment.customerName}</span>}
                  {payment.customerAni && <br />}
                  {payment.customerAni && (
                    <Link to={`/account/${encodeURIComponent(toE164(payment.customerAni))}`}>
                      {formatPhoneNumber(payment.customerAni)}
                    </Link>
                  )}
                </div>
              )}
            </>
          );
        },
        meta: {
          sortName: 'type',
          filterComponent: 'SelectFilter',
          filterOptions: INMATE_ACTIVITY_TYPE_FILTER_OPTIONS,
        },
      }),
      columnHelper.accessor((row) => row.amount, {
        id: 'amount',
        header: 'Amount',
        cell: (info) => formatCurrency(Number(info.getValue())),
        meta: { sortName: 'amount' },
      }),
      columnHelper.accessor((row) => row.description, {
        id: 'description',
        header: 'Description',
        meta: { sortName: 'description' },
      }),
    ],
    [columnHelper]
  );

  return (
    <div className="jarviswidget">
      <div className="row">
        <div className="col-md-12">
          <h5>Audio/Video balance: {balance != null ? formatCurrency(balance) : ''}</h5>

          <div className="mb-3">
            <AddActivityForm
              inmateId={inmate.id}
              accountType="audio-video"
              onSuccess={handleActivityAdded} />
          </div>

          <BaseHtmlTable
            rows={activityData}
            columnDefs={activityColumns}
            page={page}
            totalPages={totalPages}
            total={activityTotal}
            perPage={perPage}
            serverPagination={tableState.serverPagination}
            onServerPaginationChange={tableState.onServerPaginationChange}
            serverSorting={tableState.serverSorting}
            onServerSortingChange={tableState.onServerSortingChange}
            serverColumnFilters={tableState.serverColumnFilters}
            onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
            paginationMode="server"
            loading={avActivitiesResult.loading}
            pageSizeOptions={[50, 100, 200, 500]} />
        </div>
      </div>
    </div>
  );
};

export default InmateAvAccount;
